import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { RESTAURANT } from '@src/Constants';
import CustomButton from '@src/components/CustomButton/CustomButton';
import MultiSelect from '@src/components/CustomSelect/MultiSelect';
import CustomSwitch from '@src/components/CustomSwitch/CustomSwitch';
import CustomTab from '@src/components/CustomTab/CustomTab';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import CustomTooltip from '@src/components/CustomTootip/CustomTooltip';
import { BulletIcon, EyeIcon, ModalCloseIcon, QuestionMarkIcon } from '@src/components/Icons';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import DragDrop from '@src/components/ImageUploader/DragDrop';
import Loading from '@src/components/Loading/Loading';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import {
	cleanObject,
	deepEqual,
	generateUniqueID,
	validateFilePixelsAndSize,
	validateFileSize,
} from '@src/helpers/Helpers';
import { CustomAlertType, Image, Option } from '@src/interfaces/type';
import { useAppSelector } from '@src/store/hooks';
import { AxiosError, AxiosResponse } from 'axios';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MachineryDetails from '@src/pages/Settings/Components/Forms/MachineryDetails';
import PartsDetails from './PartsDetails';
import AdditionalDetails from './AdditionalDetails';
import { getEditBranchPayload } from './utils';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
`;

const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 24px;
	// width: 100%;
	height: 100%;
	padding: 24px;
	overflow: auto;
	@media screen and (max-width: 600px) {
		padding: 10px;
	}
`;

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
`;

const PreviewButton = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: '14px',
	borderRadius: '4px',
	width: '220px',
	height: '36px',
	cursor: 'pointer',
	paddingLeft: '10px',
	paddingRight: '6px',
	fontSize: '16px',
	fontWeight: 400,
	color: 'var(--default-text-color)',
	border: '1.5px solid #FFBE00',
	'&:hover': {
		background: '#FFFDF5',
	},
});

interface Branch {
	id?: number;
	name: string;
	phone?: string;
	address: string;
	supplier_create_order: boolean;
	image?: string | null;
	created_at?: string;
	organization_id?: number;
	is_deleted?: boolean;
	marketplaceStoreFrontSlug: string;
	i_source_branch_id?: number;
	i_integration_id?: string;
	i_gstin_number?: string;
	i_gst_type?: string;
	slug?: string;
	slug_approval_status?: string;
	requested_slug?: string;
	private?: boolean;
	cover?: string | null;
	gstNumber?: string;
	billingAddress?: string;
}

interface StoreSettingsProp {
	rowData: any;
	customUrl: any;
	setCustomUrl: (data: any) => void;
	images: Image[];
	setImages: Dispatch<SetStateAction<Image[]>>;
	setFiles: any;
	handleFileChange: any;
	handleFileDrop: any;
	fieldStyles: any;
	isRestaurant: boolean;
	selectedCities: any;
	setSelectedCities: any;
	cities: any;
	handleCityChange: any;
	windowDimensions: any;
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
	isUrlUpdated: any;
	isStorePrivate: boolean;
	setIsStorePrivate: (data: boolean) => void;
}

interface SettingsProps {
	branch: Branch;
	setBranch: (data: Branch) => void;
	isRestaurant: boolean;
	images: Image[];
	setImages: Dispatch<SetStateAction<Image[]>>;
	setFiles: any;
	handleFileChange: any;
	handleFileDrop: any;
	fieldStyles: any;
	windowDimensions: any;
}

const Settings: React.FC<SettingsProps> = ({
	branch,
	setBranch,
	isRestaurant,
	images,
	setImages,
	setFiles,
	handleFileChange,
	handleFileDrop,
	fieldStyles,
	windowDimensions,
}) => {
	const handleSwitchToggle = () => {
		setBranch({ ...branch, supplier_create_order: !branch?.supplier_create_order });
	};

	return (
		<StyledForm>
			<LabeledTextField
				label="Machine Owner Name"
				value={branch?.name}
				onChange={(e: any) => setBranch({ ...branch, name: e?.target?.value })}
				placeholder="e.g Lazy Suzy"
				styles={fieldStyles}
				required
			/>
			<LabeledTextField
				label="Machine Owner Address"
				value={branch?.address}
				onChange={(e: any) => setBranch({ ...branch, address: e?.target?.value })}
				placeholder="type here..."
				styles={fieldStyles}
			/>
			{isRestaurant && (
				<Box
					sx={{
						...fieldStyles,
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					<StyledLabel>Allow Dealer To Create Order</StyledLabel>
					<CustomSwitch
						title=""
						color="primary"
						checked={branch?.supplier_create_order}
						//@ts-ignore
						onToggle={handleSwitchToggle}
					/>
				</Box>
			)}

			<Box display="flex" flexDirection="column" gap="5px">
				<StyledLabel>Attach Image</StyledLabel>
				{images?.length ? (
					<ImageThumnail
						images={images}
						setImages={setImages}
						setFiles={setFiles}
						handleFileChange={handleFileChange}
						maxLength={1}
						style={{ width: windowDimensions?.width > 800 ? '100%' : '95vw' }}
					/>
				) : (
					<DragDrop
						handleFileChange={handleFileChange}
						handleFileDrop={handleFileDrop}
						text="Select image to upload"
						info="Upload image in png or jpg format."
						style={{ width: windowDimensions?.width > 800 ? '100%' : '95vw' }}
					/>
				)}
			</Box>
			<LabeledTextField
				label="GST Number"
				value={branch?.gstNumber || ''}
				onChange={(e: any) => setBranch({ ...branch, gstNumber: e?.target?.value })}
				placeholder="type here..."
				styles={fieldStyles}
			/>
			{/* <LabeledTextField
				label="Billing Address"
				value={branch?.billingAddress || ''}
				onChange={(e: any) => setBranch({ ...branch, billingAddress: e?.target?.value })}
				placeholder="type here..."
				styles={fieldStyles}
			/> */}
		</StyledForm>
	);
};

interface RestaurantEditableProps {
	rowData: any;
	setApiCall: (data: boolean) => void;
	setAlert: (data: CustomAlertType) => void;
	setOpenDrawer: (data: boolean) => void;
	displayCustomError: any;
	windowDimensions?: any;
}

const BranchEditable: FC<RestaurantEditableProps> = ({
	rowData,
	setApiCall,
	setAlert,
	setOpenDrawer,
	displayCustomError,
	windowDimensions,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const navigate = useNavigate();
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isRestaurant = currentUser?.type?.toLowerCase() === RESTAURANT;
	const marketplaceStoreFrontSlug =
		rowData?.slug_approval_status === 'APPROVED'
			? rowData?.slug
			: rowData?.requested_slug || rowData?.id;
	const [branch, setBranch] = useState<Branch>({
		name: rowData?.name,
		address: rowData?.address,
		gstNumber: rowData?.gstNumber,
		billingAddress: rowData?.billingAddress,
		image: rowData?.image,
		supplier_create_order: rowData?.supplier_create_order,
		marketplaceStoreFrontSlug: rowData?.requested_slug || marketplaceStoreFrontSlug,
	});

	const [machines, setMachines] = useState<any>(rowData?.machines?.machines || []);
	const [parts, setParts] = useState(rowData?.purchasingHabits || {});
	const [disable, setDisable] = useState(false);
	const [loading, setLoading] = useState(true);
	const [tabValue, setTabValue] = useState('1');
	//file-upload
	const [files, setFiles] = useState<File[]>([]);
	const [coverFiles, setCoverFiles] = useState<File[]>([]);
	const [cover, setCover] = useState<Image[]>(
		rowData?.cover ? [{ id: generateUniqueID(), url: rowData?.cover, hover: false }] : []
	);
	const [images, setImages] = useState<Image[]>(
		rowData?.image ? [{ id: generateUniqueID(), url: rowData?.image, hover: false }] : []
	);
	const [selectedCities, setSelectedCities] = useState<Option[]>([]);
	const [cities, setCities] = useState([]);
	const [isStorePrivate, setIsStorePrivate] = useState(rowData?.private);
	const [additionalDetails, setAdditionalDetails] = useState(rowData?.additionalProfile || {});
	const [initialData, setInitialData] = useState(null);
	const [customUrl, setCustomUrl] = useState({ available: false, url: marketplaceStoreFrontSlug });

	const fieldStyles = { width: windowDimensions?.width > 800 ? '100%' : '95vw', height: '36px' };

	const handleCityChange = (selectedOptions: Option[]) => {
		setSelectedCities(selectedOptions);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
	};

	const checkUrlChanges = () => {
		const initialData = {
			url: marketplaceStoreFrontSlug,
		};
		const updatedData = {
			url: customUrl?.url,
		};
		return deepEqual(initialData, updatedData);
	};

	const checkChanges = () => {
		const updatedDataCopy = isRestaurant
			? {
					name: branch?.name,
					address: branch?.address,
					gstNumber: branch?.gstNumber,
					billingAddress: branch?.billingAddress,
					image: images?.[0]?.url,
					supplier_create_order: branch?.supplier_create_order,
					machines: {
						numberOfMachines: machines.length,
						machines: machines,
					},
					purchasingHabits: parts,
					additionalProfile: additionalDetails,
			  }
			: {
					name: branch?.name,
					address: branch?.address,
					gstNumber: branch?.gstNumber,
					image: images?.[0]?.url,
					supplier_create_order: branch?.supplier_create_order,
					cities: selectedCities?.map((city: any) => city?.id),
					marketplaceStoreFrontSlug: customUrl?.url,
					cover: cover?.[0]?.url,
					private: isStorePrivate,
			  };

		return deepEqual(initialData, updatedDataCopy);
	};

	const disableButton = () => {
		if (!branch?.name || checkChanges()) {
			return true;
		}
		return false;
	};

	const handleSwitchToggle = () => {
		setBranch((prevState: Branch) => {
			return { ...prevState, supplier_create_order: !prevState?.supplier_create_order };
		});
	};

	const pixels = {
		height: 160,
		width: 1046,
	};

	const handleCoverFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const files = event?.target?.files;
		let imageData = [];
		if (files?.length) {
			const validFile = await validateFilePixelsAndSize(
				files?.[0],
				pixels,
				['jpg', 'png', 'jpeg'],
				3 * 1024 * 1024
			);
			console.log(validFile?.toLowerCase());
			if (validFile?.toLowerCase() === 'allowed') {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				if (validFile?.toLowerCase() === 'pixelexceeds') {
					displayCustomError('Incorrect Dimensions');
					return;
				} else if (validFile?.toLowerCase() === 'sizeexceeds') {
					displayCustomError('File Size Exceeds');
					return;
				}
				displayCustomError('Something went wrong!');
			}
			setCoverFiles([files?.[0]]);
			setCover(imageData);
		}
	};

	const handleCoverFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const files = event.dataTransfer?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setCoverFiles([files?.[0]]);
			setCover(imageData);
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();
		const files = event?.target?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setFiles([files?.[0]]);
			setImages(imageData);
		}
	};
	const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const files = event.dataTransfer?.files;
		let imageData = [];
		if (files?.length) {
			const validSize = validateFileSize(files?.[0].size, 2 * 1024 * 1024);
			if (validSize) {
				const fileURL = URL.createObjectURL(files?.[0]);
				imageData.push({ id: generateUniqueID(), url: fileURL, hover: false });
			} else {
				displayCustomError('File Size Exceeds');
			}
			setFiles([files?.[0]]);
			setImages(imageData);
		}
	};

	const uploadImage = () => {
		const file = files[0];
		const formData = new FormData();
		formData.append('file', file);
		const uploadPromise: any = new Promise((resolve, reject) => {
			apiService?.uploadBranchImage(
				formData,
				(response: AxiosResponse) => {
					resolve(response?.data?.data?.url);
				},
				(error: AxiosError) => {
					displayCustomError('Something went wrong');
					reject(error);
				}
			);
		});
		return uploadPromise;
	};
	const uploadCover = () => {
		const file = coverFiles[0];
		const formData = new FormData();
		formData.append('file', file);
		const uploadPromise: any = new Promise((resolve, reject) => {
			apiService?.uploadBranchImage(
				formData,
				(response: AxiosResponse) => {
					resolve(response?.data?.data?.url);
				},
				(error: AxiosError) => {
					displayCustomError('Something went wrong');
					reject(error);
				}
			);
		});
		return uploadPromise;
	};

	const getImageValue = (image: string) => {
		if (image) {
			return image;
		}
		if (!rowData?.image) {
			return null;
		}
		return rowData?.image && !images?.length ? null : rowData?.image;
	};

	const getCoverValue = (image: string) => {
		if (image) {
			return image;
		}
		if (!rowData?.cover) {
			return null;
		}
		return rowData?.cover && !images?.length ? null : rowData?.cover;
	};

	const editBranch = async (branch: Branch) => {
		setDisable(true);

		const restaurantImage = files?.length ? await uploadImage() : '';
		const coverImage = coverFiles?.length ? await uploadCover() : '';

		const props = {
			isRestaurant,
			branch,
			getImageValue,
			getCoverValue,
			restaurantImage,
			coverImage,
			customUrl,
			selectedCities,
			isStorePrivate,
			machines,
			parts,
			additionalDetails,
		};

		const data = getEditBranchPayload(props);

		apiService.editBranch(
			data,
			(response: AxiosResponse) => {
				setAlert({
					message: `${isRestaurant ? 'Dealer' : 'Supplier'} Updated Successfully`,
					severity: 'success',
				});
				setDisable(false);
				setApiCall(true);
				setOpenDrawer(false);
			},
			(error: AxiosError) => {
				const errorMessage = error?.response?.data?.message || 'Something went wrong!';
				displayCustomError(errorMessage);

				setDisable(false);
			},
			rowData?.id
		);
	};

	const getCities = () => {
		apiService.getCitiesForMarketplaceSuppliers(
			(res: AxiosResponse) => {
				const data = res?.data?.data?.cities?.map((city: any) => {
					return {
						id: city?.city_id,
						value: city?.city_name,
					};
				});
				const selectedCitiesIds = rowData?.cities?.map((city: any) => city?.city_id);
				const initialData = isRestaurant
					? {
							name: rowData?.name,
							address: rowData?.address,
							gstNumber: rowData?.gstNumber,
							billingAddress: rowData?.billingAddress,
							image: rowData?.image ? rowData?.image : undefined,
							supplier_create_order: rowData?.supplier_create_order,
							machines: {
								numberOfMachines: rowData?.machines?.machines?.length,
								machines: rowData?.machines?.machines,
							},
							purchasingHabits: rowData?.purchasingHabits,
							additionalProfile: rowData?.additionalProfile,
					  }
					: {
							name: rowData?.name,
							address: rowData?.address,
							gstNumber: rowData?.gstNumber,
							billingAddress: rowData?.billingAddress,
							image: rowData?.image ? rowData?.image : undefined,
							supplier_create_order: rowData?.supplier_create_order,
							cities: selectedCitiesIds,
							marketplaceStoreFrontSlug: rowData?.requested_slug || marketplaceStoreFrontSlug,
							cover: rowData?.cover ? rowData?.cover : undefined,
							private: rowData?.private,
					  };

				const selectedOptions = data?.filter((city: any) => selectedCitiesIds?.includes(city?.id));
				setSelectedCities(selectedOptions);
				setCities(data);
				//@ts-ignore
				setInitialData(initialData);
				setLoading(false);
			},
			(error: AxiosError) => {
				displayCustomError('Something went wrong!');
				setLoading(false);
			}
		);
	};
	useEffect(() => {
		getCities();
	}, []);

	const tabs = [
		{
			label: "Machine Owner's Setting",
			value: '1',
			content: (
				<Settings
					branch={branch}
					setBranch={setBranch}
					isRestaurant={isRestaurant}
					images={images}
					setImages={setImages}
					setFiles={setFiles}
					handleFileChange={handleFileChange}
					handleFileDrop={handleFileDrop}
					fieldStyles={fieldStyles}
					windowDimensions={windowDimensions}
				/>
			),
		},
		{
			label: 'Machinery Details',
			value: '2',
			content: (
				<StyledForm>
					<MachineryDetails machines={machines} setMachines={setMachines} />
				</StyledForm>
			),
		},
		{
			label: 'Parts Details',
			value: '3',
			content: (
				<StyledForm>
					<PartsDetails rowData={rowData} parts={parts} setParts={setParts} />
				</StyledForm>
			),
		},
		{
			label: 'Additional Details',
			value: '4',
			content: (
				<StyledForm>
					<AdditionalDetails
						additionalDetails={additionalDetails}
						setAdditionalDetails={setAdditionalDetails}
					/>
				</StyledForm>
			),
		},
	];

	if (loading) {
		return <Loading />;
	}
	return (
		<StyledContainer>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				p={2}
				width="100%"
				height="56px"
			>
				<Typography variant="body1">{`Edit ${
					isRestaurant ? 'Dealer' : 'Supplier'
				} Information`}</Typography>
				<Box display="flex" alignItems="center" gap="15px">
					<CustomButton
						title={
							disable ? (
								<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
							) : (
								'Update'
							)
						}
						color="primary"
						styles={{ height: '36px', width: '80px' }}
						handleClick={() => editBranch(branch)}
						disabled={disableButton()}
					/>
					<Box sx={{ cursor: 'pointer' }} onClick={() => setOpenDrawer(false)}>
						<ModalCloseIcon />
					</Box>
				</Box>
			</Box>
			<Divider style={{ width: '100%' }} />
			{/* {isRestaurant ? (
				<Settings
					branch={branch}
					setBranch={setBranch}
					isRestaurant={isRestaurant}
					images={images}
					setImages={setImages}
					setFiles={setFiles}
					handleFileChange={handleFileChange}
					handleFileDrop={handleFileDrop}
					fieldStyles={fieldStyles}
					windowDimensions={windowDimensions}
				/>
			) : (
				<CustomTab tabValue={tabValue} handleTabChange={handleTabChange} tabs={tabs} />
			)} */}
			{isRestaurant ? (
				<CustomTab tabValue={tabValue} handleTabChange={handleTabChange} tabs={tabs} />
			) : (
				<Settings
					branch={branch}
					setBranch={setBranch}
					isRestaurant={isRestaurant}
					images={images}
					setImages={setImages}
					setFiles={setFiles}
					handleFileChange={handleFileChange}
					handleFileDrop={handleFileDrop}
					fieldStyles={fieldStyles}
					windowDimensions={windowDimensions}
				/>
			)}
		</StyledContainer>
	);
};

export default BranchEditable;
